import { Song } from "@/models/Song";
import { DataType } from "@/models/DataType";
import Arrangement from "@/models/Arrangement";
import { LoadStatus } from "@/models/frontend-only/LoadStatus";
import getYouTubeID from "get-youtube-id";
import { CustomError } from "@/models/frontend-only/CustomError";
import { EmailSettings } from "@/models/frontend-only/EmailSettings";
import { FEMediaItem } from "@/models/frontend-only/FEMediaItem";
import { FESong } from "@/models/frontend-only/FESong";
import { SongVersionItem } from "@/models/frontend-only/SongVersion";

export const useSongStore = defineStore("song", () => {
  const appStore = useAppStore();
  const route = useRoute();
  const api = useApiStore();
  const profileStore = useProfileStore();

  const fullSongStore = useFullSongPlaybackStore();
  const busy = useBusyStore();
  const debug = useDebug();
  const { t } = useI18n();
  const status = ref<LoadStatus>(LoadStatus.Loading);
  const toast = useToastStore();
  const showCopyButton = computed(() => !!navigator?.clipboard?.writeText);

  const dataType = computed(() =>
    route.path.includes("/arrangements/")
      ? DataType.Arrangement
      : DataType.Song,
  );
  const arrangementType = computed(
    () => dataType.value === DataType.Arrangement,
  );
  const title = computed(() =>
    arrangementType.value
      ? arrangement.value?.title || ""
      : song.value?.title || "",
  );

  const loading = ref<boolean>(false); // To be removed altogether and replaced with 'status'
  const song = ref<FESong | null>(null);
  const media = ref<FEMediaItem[]>([]);
  const arrangement = ref<Arrangement | null>(null);
  const data = computed(() =>
    arrangementType.value ? arrangement.value : song.value,
  );

  const bgSplashImgUrl = ref("");

  const defaultKey = computed(() =>
    data.value?.defaultKey?.length ? data.value.defaultKey[0] : "",
  );

  const copyLyricsContent = computed(() => {
    if (data.value && data.value.lyrics) {
      const licenseText = profileStore.profile?.licenseNumber
        ? t("G.CcliLicenseNumber") + profileStore.profile?.licenseNumber
        : "";
      return [
        data.value.title,
        ...data.value.lyrics.map(
          (l) =>
            l.partLabel +
            "\n" +
            l.lyrics
              .split("|")
              .map((ll) => ll.trim())
              .join("\n"),
        ),
        [
          data.value.authors.map((a) => a.label).join(", "),
          t("G.CcliSongNumber") + data.value.ccliSongNumber,
          data.value.copyrights
            ?.replace(/(\r\n|\n|\r)/gm, "")
            .replace(/\s+/g, " "),
          t("G.MES_SongSelectDisclaimer"),
          //t("G.CcliLicenseNumber") + profileStore.profile?.licenseNumber,
          licenseText,
        ].join("\n"),
      ].join("\n\n");
    }
    return "";
  });

  async function _getSongDetails(
    songNumber: string,
    slug: string,
    // arrangementNumber: string
  ) {
    songNotFound.value = false;
    status.value = LoadStatus.Loading;
    busy.showBusy();
    song.value = null;
    try {
      const songResponse = (await api.get("/GetSongDetails", {
        songNumber,
        slug,
      })) as Song;

      let songVersion: SongVersionItem | null = null;
      if (songResponse?.media.length) {
        songResponse.media.forEach((m, mIndex) => {
          debug.log("media item", m);

          if (m.type === "song") {
            // m.urls.forEach((url, urlIndex) => {
            //   url.source = "url";
            //   url.url = m.previewUrl || url.url;
            // });
          } else if (m.type === "video") {
            m.urls.forEach((url) => {
              url.source = "youtube";
              let videoId = getYouTubeID(url.url);
              if (!videoId && url.url.includes("embed")) {
                videoId = url.url.split("/").pop() || "";
              }
              if (!videoId) {
                return;
              }
              const thumbnail = `https://img.youtube.com/vi/${videoId}/0.jpg`;
              songResponse.media[mIndex].artwork = thumbnail;
            });
          }
        });

        if (songResponse.ccliSongNumber) {
          songVersion = await getSongVersions(songResponse.ccliSongNumber);
          if (songVersion) {
            songResponse.title = songVersion.SongTitle;
            songResponse.media = [
              {
                applesongid: songVersion.AppleRecordingID,
                isrc: songVersion.ISRC,
                artwork: songVersion.AlbumArtURL,
                previewUrl: songVersion.SoundSampleURL,
                title: songVersion.Album,
                id: "R01",
                previewLengthSeconds: 0,
                lengthSeconds: 0,
                subtitle: songVersion.Artist,
                type: "song",
                arrangementNumber: null,
                urls: [
                  {
                    source: "iTunes",
                    url: songVersion.AlbumURL,
                    id: songVersion.AppleRecordingID,
                  },
                ],
              },
              ...songResponse.media.filter((m) => m.type === "video"),
            ];
          }
        }
      }

      bgSplashImgUrl.value =
        songResponse?.media?.find((m) => m.type === "song")?.artwork || "";
      song.value = Object.assign(
        {
          enableSongVersion: !!songVersion,
          album: songVersion?.Album || null,
          artist: songVersion?.Artist || null,
        },
        songResponse,
      );

      await setupMedia();
      status.value = LoadStatus.Ready;
    } catch (err) {
      if ((err as CustomError)?.customData?.err?.response?.status === 404) {
        songNotFound.value = true;
      }
      status.value = LoadStatus.Error;
      throw err;
    }
    busy.hideBusy();
    status.value = LoadStatus.Ready;
  }
  const songNotFound = ref(false);
  let getSongDetailsPromise: Promise<void> | null = null;
  watch(() => fullSongStore.integration, setupMedia);

  async function setupMedia() {
    let _media = song.value?.media || [];
    if (!_media.length) {
      media.value = [];
      return;
    }

    //only get unique isrcs
    const isrcs = [
      ...new Set(
        _media.filter((m) => m.type !== "video" && m.isrc).map((m) => m.isrc),
      ),
    ];

    const theRest = _media.filter((m) => m.type === "video" || !m.isrc);
    _media = [
      ...isrcs.map((isrc) => _media.find((m) => m.isrc === isrc)!),
      ...theRest,
    ];

    if (fullSongStore.integration === "spotify") {
      const promises: Promise<void>[] = [];

      const spotifyStore = useSpotifyStore();
      _media.forEach((m, i) => {
        if (m.type === "song" && m.isrc) {
          if (
            (song.value?.enableSongVersion && i === 0) ||
            !song.value?.enableSongVersion
          ) {
            promises.push(
              spotifyStore.getSongFromISRC(m.isrc).then((spotifyItem) => {
                if (spotifyItem) {
                  m.urls.push(spotifyItem);
                  m.title = spotifyItem.title || m.title;
                  m.subtitle = spotifyItem.subtitle || m.subtitle;
                  m.artwork = spotifyItem.artwork || m.artwork;
                }
              }),
            );
          }
        }
      });
      await Promise.all(promises);

      _media = _media.filter(
        (m) =>
          m.type === "video" ||
          (m.type === "song" && !!m.urls.find((u) => u.source === "Spotify")) ||
          !!m.urls.find((u) => u.source === "SoundSample"),
      );
    }
    if (fullSongStore.integration === "apple") {
      _media = _media.filter(
        (m) =>
          m.type === "video" ||
          (m.type === "song" && !!m.urls.find((u) => u.source === "iTunes")) ||
          !!m.urls.find((u) => u.source === "SoundSample"),
      );
    }
    media.value = _media as FEMediaItem[];
    console.timeEnd("setup-media");
  }

  async function getSongDetails(songNumber: string, slug: string) {
    if (!getSongDetailsPromise) {
      getSongDetailsPromise = _getSongDetails(songNumber, slug).finally(() => {
        getSongDetailsPromise = null;
      });
    }
    await getSongDetailsPromise;
    return;
  }

  /*  async function getArrangementDetails(
    songNumber: string,
    slug: string,
    arrangementNumber: string
  ) {
    status.value = LoadStatus.Loading;
    busy.showBusy();
    try {
      arrangement.value = (await api.post("/GetArrangementDetails", {
        songNumber,
        slug,
        arrangementNumber,
      })) as Arrangement;
    } catch (err) {
      status.value = LoadStatus.Error;
      throw err;
    }
    busy.hideBusy();
    status.value = LoadStatus.Ready;
  }*/

  const onLyricsActivityDebounced = useDebounceFn(onLyricsActivity, 2000);

  function onLyricsActivity(
    activityType: "copied" | "printed" | "downloaded" | "shared" | "emailed",
  ) {
    //no await necessary here since we don't want this to be blocking in any way
    if (
      !song.value ||
      !song.value.lyrics ||
      status.value === LoadStatus.Error ||
      status.value === LoadStatus.Loading
    ) {
      return;
    }
    api.post("/RecordSongLyricsActivity", {
      ccliSongNumber: song.value.ccliSongNumber,
      activityType,
    });
  }

  async function copyToClipboard(content: string) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(content || "");
    } else {
      toast.errorToast(t("SV.MES_EnableCopy"));
    }
  }

  function updateSongdetailsMeta() {
    const hasLyrics = song.value?.products.lyrics.exists;
    const hasChords = song.value?.products.chords.exists;
    const authors = song.value?.authors.map((i) => i.label).join(", ");
    let metaContent = "";

    if (song.value?.artist) {
      metaContent = t(
        "S.LIT_PageDescriptionSEO_SongWithChordLyricAndSheetMusicByAuthor",
        [title.value, authors],
      );
    } else {
      if (hasChords) {
        if (hasLyrics) {
          metaContent = t(
            "S.LIT_PageDescriptionSEO_SongWithChordLyricAndResources",
            [title.value, authors],
          );
        } else {
          metaContent = t(
            "S.LIT_PageDescriptionSEO_SongWithChordAndResources",
            [title.value, authors],
          );
        }
      } else {
        if (hasLyrics) {
          metaContent = t(
            "S.LIT_PageDescriptionSEO_SongWithLyricAndResources",
            [title.value, authors],
          );
        } else {
          metaContent = t("S.LIT_PageDescriptionSEO_SongWithResources", [
            title.value,
            authors,
          ]);
        }
      }
    }

    appStore.updateDescription(metaContent);
  }

  const placeholderNotLoaded = computed(
    () => status.value !== LoadStatus.Ready,
  );
  const placeholderNotAuthorized = computed(
    () =>
      placeholderNotLoaded.value ||
      (status.value === LoadStatus.Ready &&
        !song.value?.products.general.authorized),
  );

  const modals = useModalStore();

  function showMaxSongUsage() {
    modals.showModal("MaxSongModal");
  }

  const emailSettings = ref<EmailSettings>({
    product: "",
    songKey: null,
    style: null,
    columns: null,
    octave: null,
    noteSize: null,
    orientation: null,
    paperSize: null,
    busy: false,
    renderer: null,
  });

  async function emailSong(settings: {
    product: string;
    songKey: string | null;
    style: string | null;
    columns: string | null;
    octave: string | null;
    noteSize: string | null;
    orientation: string | null;
    paperSize: string | null;
    renderer?: string | null;
    enableFontCustomization?: boolean;
    css?: string;
  }) {
    emailSettings.value.busy = true;

    emailSettings.value.product = settings.product;
    emailSettings.value.songKey = settings.songKey;
    emailSettings.value.style = settings.style;
    emailSettings.value.columns = settings.columns;
    emailSettings.value.octave = settings.octave;
    emailSettings.value.noteSize = settings.noteSize;
    emailSettings.value.orientation = settings.orientation;
    emailSettings.value.paperSize = settings.paperSize;
    emailSettings.value.renderer = settings.renderer;
    emailSettings.value.enableFontCustomization =
      settings.enableFontCustomization;
    emailSettings.value.css = settings.css;

    await profileStore.getProfile();

    const emails = profileStore.profile?.availableEmails || [];

    if (emails.length >= 2) {
      modals.showModal("SelectEmailModal");
    } else if (emails.length === 1) {
      await submitEmailSong(emails[0], true);
    } else {
      toast.errorToast(t("SV.MES_NoEmailsAssociated"));
    }

    emailSettings.value.busy = false;
  }

  async function submitEmailSong(email: string, ignoreBusy = false) {
    if (!ignoreBusy) {
      emailSettings.value.busy = true;
    }
    if (!song.value) {
      return;
    }
    const params: { [key: string]: string } = {
      email,
      songNumber: song.value.ccliSongNumber || "",
      product: emailSettings.value.product,
      key: emailSettings.value.songKey || "",
      style: emailSettings.value.style || "",
      columns: emailSettings.value.columns || "",
      octave: emailSettings.value.octave || "",
      noteSize: emailSettings.value.noteSize || "",
      orientation: emailSettings.value.orientation || "",
      paperSize: emailSettings.value.paperSize || "",
    };

    if (emailSettings.value.enableFontCustomization !== undefined) {
      params["enableFontCustomization"] = emailSettings.value
        .enableFontCustomization
        ? "1"
        : "0";
    }
    if (emailSettings.value.enableFontCustomization) {
      params["css"] = emailSettings.value.css || "";
    }

    await api.post("/EmailSong", params);

    if (
      emailSettings.value.product === "lead" ||
      emailSettings.value.product === "vocal"
    ) {
      params["renderer"] = emailSettings.value.renderer || "legacy";
    }

    if (emailSettings.value.product === "lyrics") {
      onLyricsActivityDebounced("emailed");
    }
    if (!ignoreBusy) {
      emailSettings.value.busy = false;
    }
    modals.hideModal();
    toast.successToast(t("SV.LIT_SongSentTo", { email: email }));
  }

  return {
    submitEmailSong,
    emailSong,
    emailSettings,
    arrangementType,
    placeholderNotLoaded,
    placeholderNotAuthorized,
    dataType,
    status,
    song,
    loading,
    arrangement,
    data,
    defaultKey,
    title,
    getSongDetails,
    //getArrangementDetails,
    copyLyricsContent,
    onLyricsActivity,
    getSongDetailsPromise,
    showMaxSongUsage,
    songNotFound,
    copyToClipboard,
    showCopyButton,
    media,
    bgSplashImgUrl,
    //status,
    updateSongdetailsMeta,
    onLyricsActivityDebounced,
  };
});
